/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { defaultMountHeight, normalizeMountType } from './apMounts'
import type { ApMountType } from './types'
import { clampApPosition, clampToFloorBounds, floorPlacementBounds, isServiceableCeilingApSite, pointInFloorBounds } from './floorBounds'
import { resolveObstacleFootprint } from './obstacles'
import type { FloorGeometry, FloorWall, PlacedAp } from './types'
import { computeFloorMetrics, type FloorPlanMetrics } from './rfFloorMetrics'
import { weakZoneCentroid } from './rfRecommendationFixes'
import { computeHeatmapGrid } from './rfSimulation'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}

function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-6) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

/** Ceiling/desk APs should not sit on partition centerlines. */
export function pointOnWallLine(
  x: number,
  y: number,
  walls: FloorWall[],
  thresholdPct = 1.6,
): boolean {
  for (const w of walls) {
    if (distToSegment(x, y, w.x1, w.y1, w.x2, w.y2) <= thresholdPct) return true
  }
  return false
}

function roomContaining(x: number, y: number, geometry: FloorGeometry) {
  for (let i = geometry.rooms.length - 1; i >= 0; i--) {
    const r = geometry.rooms[i]
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r
  }
  return null
}

export function ensureApMountDefaults(aps: PlacedAp[]): PlacedAp[] {
  return aps.map(ap => ({
    ...ap,
    mount: normalizeMountType(ap.mount),
    mountHeightM: ap.mountHeightM ?? defaultMountHeight(ap.mount),
  }))
}

/** Reject AP positions inside metal racks / obstacle footprints. */
export function pointInsideObstacle(
  x: number,
  y: number,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  marginPct = 1.2,
): boolean {
  for (const o of geometry.obstacles) {
    const fp = resolveObstacleFootprint(o, floorWidthM, floorHeightM)
    const hw = fp.w / 2 + marginPct
    const hh = fp.h / 2 + marginPct
    if (x >= o.x - hw && x <= o.x + hw && y >= o.y - hh && y <= o.y + hh) return true
  }
  return false
}

function orientationToward(fromX: number, fromY: number, toX: number, toY: number): number {
  let deg = (Math.atan2(toY - fromY, toX - fromX) * 180) / Math.PI
  if (deg < 0) deg += 360
  return Math.round(deg)
}

function wallInwardOffset(wall: FloorWall, floorWidthM: number, floorHeightM: number, toward: { x: number; y: number }) {
  const mx = (wall.x1 + wall.x2) / 2
  const my = (wall.y1 + wall.y2) / 2
  const dx = wall.x2 - wall.x1
  const dy = wall.y2 - wall.y1
  const len = Math.hypot(dx, dy) || 1
  let nx = -dy / len
  let ny = dx / len
  const toMx = toward.x - mx
  const toMy = toward.y - my
  if (nx * toMx + ny * toMy < 0) {
    nx = -nx
    ny = -ny
  }
  const offsetPct = Math.max(1.2, (0.3 / floorWidthM) * 100)
  return {
    x: clamp(mx + (nx * offsetPct * floorWidthM) / floorWidthM * 100, 4, 96),
    y: clamp(my + (ny * offsetPct * floorHeightM) / floorHeightM * 100, 4, 96),
    orientDeg: orientationToward(mx, my, toward.x, toward.y),
  }
}

function coverageTargets(geometry: FloorGeometry, weakCentroid: { x: number; y: number } | null) {
  const targets: { x: number; y: number }[] = []
  if (weakCentroid) targets.push(weakCentroid)
  for (const room of geometry.rooms) {
    targets.push({ x: room.x + room.w / 2, y: room.y + room.h / 2 })
  }
  if (targets.length === 0) targets.push({ x: 50, y: 50 })
  return targets
}

/** Candidate x/y/orientation positions respecting mount type and obstacles. */
export function placementCandidates(
  ap: PlacedAp,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  weakCentroid: { x: number; y: number } | null,
): Partial<PlacedAp>[] {
  const mount = normalizeMountType(ap.mount)
  const targets = coverageTargets(geometry, weakCentroid)
  const bounds = floorPlacementBounds(geometry)
  const seen = new Set<string>()
  const out: Partial<PlacedAp>[] = []

  function add(c: Partial<PlacedAp>) {
    const rawX = c.x ?? ap.x
    const rawY = c.y ?? ap.y
    const { x, y } = clampToFloorBounds(rawX, rawY, geometry)
    const key = `${x.toFixed(1)},${y.toFixed(1)},${c.mountOrientationDeg ?? ap.mountOrientationDeg ?? -1}`
    if (seen.has(key)) return
    if (!pointInFloorBounds(x, y, geometry)) return
    if (pointInsideObstacle(x, y, geometry, floorWidthM, floorHeightM)) return
    if (mount !== 'wall' && pointOnWallLine(x, y, geometry.walls)) return
    if (!isServiceableCeilingApSite(x, y, geometry, mount)) return
    seen.add(key)
    out.push({ ...c, x, y })
  }

  add({ x: ap.x, y: ap.y, mountOrientationDeg: ap.mountOrientationDeg })

  if (mount === 'wall' && geometry.walls.length > 0) {
    for (const wall of geometry.walls) {
      for (const target of targets.slice(0, 4)) {
        const snap = wallInwardOffset(wall, floorWidthM, floorHeightM, target)
        add({
          x: snap.x,
          y: snap.y,
          mountOrientationDeg: snap.orientDeg,
          mountHeightM: ap.mountHeightM ?? defaultMountHeight('wall'),
        })
      }
    }
  } else if (mount === 'ceiling' && geometry.rooms.length > 0) {
    const home = roomContaining(ap.x, ap.y, geometry)
    if (home) {
      add({
        x: home.x + home.w / 2,
        y: home.y + home.h / 2,
        mountHeightM: ap.mountHeightM ?? defaultMountHeight('ceiling'),
      })
    }
    for (const room of geometry.rooms) {
      add({
        x: room.x + room.w / 2,
        y: room.y + room.h / 2,
        mountHeightM: ap.mountHeightM ?? defaultMountHeight('ceiling'),
      })
      for (const target of targets.slice(0, 3)) {
        const cx = room.x + room.w / 2
        const cy = room.y + room.h / 2
        for (const step of [0.25, 0.5, 0.75]) {
          add({
            x: clamp(cx + (target.x - cx) * step, room.x + 2, room.x + room.w - 2),
            y: clamp(cy + (target.y - cy) * step, room.y + 2, room.y + room.h - 2),
            mountHeightM: ap.mountHeightM ?? defaultMountHeight('ceiling'),
          })
        }
      }
    }
  } else if (mount === 'outdoor') {
    const edgeTargets = [
      { x: bounds.xMin + 4, y: ap.y },
      { x: bounds.xMax - 4, y: ap.y },
      { x: ap.x, y: bounds.yMin + 4 },
      { x: ap.x, y: bounds.yMax - 4 },
      ...targets,
    ]
    for (const target of edgeTargets) {
      for (const step of [0.2, 0.45, 0.7, 1]) {
        add({
          x: ap.x + (target.x - ap.x) * step,
          y: ap.y + (target.y - ap.y) * step,
          mountHeightM: ap.mountHeightM ?? defaultMountHeight('outdoor'),
        })
      }
    }
  } else {
    for (const target of targets) {
      for (const step of [0.12, 0.22, 0.35, 0.5, 0.65]) {
        add({
          x: ap.x + (target.x - ap.x) * step,
          y: ap.y + (target.y - ap.y) * step,
        })
      }
    }
    if (mount === 'desk') {
      add({ x: ap.x, y: ap.y, mountHeightM: ap.mountHeightM ?? defaultMountHeight('desk') })
    } else {
      add({ x: ap.x, y: ap.y, mountHeightM: ap.mountHeightM ?? defaultMountHeight(mount) })
    }
  }

  return out
}

/** Valid floor position for mount type — rejects obstacle footprints and partition lines. */
export function isValidApPosition(
  x: number,
  y: number,
  mount: ApMountType,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): boolean {
  if (!pointInFloorBounds(x, y, geometry)) return false
  if (pointInsideObstacle(x, y, geometry, floorWidthM, floorHeightM)) return false
  if (mount !== 'wall' && pointOnWallLine(x, y, geometry.walls)) return false
  if (!isServiceableCeilingApSite(x, y, geometry, mount)) return false
  return true
}

function nearestWall(walls: FloorWall[], x: number, y: number): FloorWall | null {
  if (walls.length === 0) return null
  let best = walls[0]
  let bestDist = Infinity
  for (const wall of walls) {
    const d = distToSegment(x, y, wall.x1, wall.y1, wall.x2, wall.y2)
    if (d < bestDist) {
      bestDist = d
      best = wall
    }
  }
  return best
}

/**
 * After an x/y move, snap wall mounts to partitions, keep mount height, and aim boresight
 * toward weak zones. Rejects moves into obstacles or onto wall centerlines (non-wall mounts).
 */
export function reconcileApAfterMove(
  ap: PlacedAp,
  targetX: number,
  targetY: number,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  aimTarget?: { x: number; y: number } | null,
): PlacedAp {
  const mount = normalizeMountType(ap.mount)
  const mountHeightM = ap.mountHeightM ?? defaultMountHeight(mount)
  const bounded = clampApPosition(targetX, targetY, mount, geometry)
  const toward = aimTarget ?? { x: 50, y: 50 }

  if (mount === 'wall' && geometry.walls.length > 0) {
    const wall = nearestWall(geometry.walls, bounded.x, bounded.y) ?? geometry.walls[0]
    const snap = wallInwardOffset(wall, floorWidthM, floorHeightM, toward)
    return {
      ...ap,
      mount,
      mountHeightM,
      x: snap.x,
      y: snap.y,
      mountOrientationDeg: snap.orientDeg,
    }
  }

  if (!isValidApPosition(bounded.x, bounded.y, mount, geometry, floorWidthM, floorHeightM)) {
    if (isValidApPosition(ap.x, ap.y, mount, geometry, floorWidthM, floorHeightM)) {
      return { ...ap, mount, mountHeightM }
    }
    return { ...ap, mount, mountHeightM, x: bounded.x, y: bounded.y }
  }

  return {
    ...ap,
    mount,
    mountHeightM,
    x: bounded.x,
    y: bounded.y,
    mountOrientationDeg:
      mount === 'wall' && aimTarget
        ? orientationToward(bounded.x, bounded.y, aimTarget.x, aimTarget.y)
        : ap.mountOrientationDeg,
  }
}

function scoreMetrics(m: FloorPlanMetrics): number {
  return m.score
}

function tryImproveAp(
  working: PlacedAp[],
  apIndex: number,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  weakCentroid: { x: number; y: number } | null,
): PlacedAp[] {
  const ap = working[apIndex]
  const before = computeFloorMetrics(working, geometry, floorWidthM, floorHeightM)
  let best = working
  let bestScore = scoreMetrics(before)

  for (const cand of placementCandidates(ap, geometry, floorWidthM, floorHeightM, weakCentroid)) {
    const merged: PlacedAp = {
      ...ap,
      mount: normalizeMountType(ap.mount),
      x: cand.x ?? ap.x,
      y: cand.y ?? ap.y,
      mountOrientationDeg: cand.mountOrientationDeg ?? ap.mountOrientationDeg,
      mountHeightM: cand.mountHeightM ?? ap.mountHeightM ?? defaultMountHeight(ap.mount),
    }
    const trial = working.map((item, j) => (j === apIndex ? merged : item))
    const after = computeFloorMetrics(trial, geometry, floorWidthM, floorHeightM)
    const s = scoreMetrics(after)
    if (s > bestScore + 0.15 || (after.weakPct < before.weakPct - 0.3 && s >= bestScore - 0.5)) {
      best = trial
      bestScore = s
    }
  }
  return best
}

/** Reposition APs using full RF sim — walls, obstacle height, mount type & wall boresight. */
export function optimizeGeometryAwarePositions(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  let next = ensureApMountDefaults(working)
  for (let pass = 0; pass < 4; pass++) {
    const grid = computeHeatmapGrid(next, geometry, floorWidthM, floorHeightM, RF_GRID_COLS, RF_GRID_ROWS)
    const weakCentroid = weakZoneCentroid(grid, geometry, floorWidthM, floorHeightM)
    const before = computeFloorMetrics(next, geometry, floorWidthM, floorHeightM, grid)
    let improved = false
    for (let i = 0; i < next.length; i++) {
      const trial = tryImproveAp(next, i, geometry, floorWidthM, floorHeightM, weakCentroid)
      const after = computeFloorMetrics(trial, geometry, floorWidthM, floorHeightM)
      if (after.score > before.score + 0.1 || after.weakPct < before.weakPct - 0.25) {
        next = trial
        improved = true
      }
    }
    if (!improved) break
  }
  return next
}

/** Sweep wall-mount boresight toward weak zones / rooms (uses directional pattern in RF sim). */
export function optimizeWallMountOrientations(
  working: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  const grid = computeHeatmapGrid(working, geometry, floorWidthM, floorHeightM, RF_GRID_COLS, RF_GRID_ROWS)
  const weakCentroid = weakZoneCentroid(grid, geometry, floorWidthM, floorHeightM)
  const targets = coverageTargets(geometry, weakCentroid)

  return working.map(ap => {
    if (normalizeMountType(ap.mount) !== 'wall') return ap
    const before = computeFloorMetrics(working, geometry, floorWidthM, floorHeightM)
    let bestAp = { ...ap, mountHeightM: ap.mountHeightM ?? defaultMountHeight('wall') }
    let bestScore = before.score
    let bestWeak = before.weakPct

    const orientations = new Set<number>([ap.mountOrientationDeg ?? 0])
    for (const t of targets) {
      orientations.add(orientationToward(ap.x, ap.y, t.x, t.y))
    }
    for (let deg = 0; deg < 360; deg += 45) orientations.add(deg)

    for (const orient of orientations) {
      const trialAps = working.map(a =>
        a.id === ap.id
          ? {
              ...a,
              mount: 'wall' as const,
              mountHeightM: a.mountHeightM ?? defaultMountHeight('wall'),
              mountOrientationDeg: orient,
            }
          : a,
      )
      const after = computeFloorMetrics(trialAps, geometry, floorWidthM, floorHeightM)
      if (after.score > bestScore + 0.05 || after.weakPct < bestWeak - 0.2) {
        bestAp = {
          ...ap,
          mount: 'wall',
          mountHeightM: ap.mountHeightM ?? defaultMountHeight('wall'),
          mountOrientationDeg: orient,
        }
        bestScore = after.score
        bestWeak = after.weakPct
      }
    }
    return bestAp
  })
}

/** Place APs at room centers / clear grid cells — skips obstacle footprints. */
export function redistributeApsGeometryAware(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): PlacedAp[] {
  const n = aps.length
  if (n < 2) return aps

  const bounds = floorPlacementBounds(geometry)
  const slots: { x: number; y: number }[] = []

  for (const room of geometry.rooms) {
    const cx = clamp(room.x + room.w / 2, bounds.xMin, bounds.xMax)
    const cy = clamp(room.y + room.h / 2, bounds.yMin, bounds.yMax)
    slots.push({ x: cx, y: cy })
  }

  if (slots.length < n) {
    const aspect = floorWidthM / Math.max(1, floorHeightM)
    const cols = Math.max(2, Math.ceil(Math.sqrt(n * aspect)))
    const rows = Math.ceil(n / cols)
    const spanX = bounds.xMax - bounds.xMin
    const spanY = bounds.yMax - bounds.yMin
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (slots.length >= n * 2) break
        slots.push({
          x: bounds.xMin + ((c + 0.5) / cols) * spanX,
          y: bounds.yMin + ((r + 0.5) / rows) * spanY,
        })
      }
    }
  }

  const validSlots = slots.filter(
    s =>
      pointInFloorBounds(s.x, s.y, geometry) &&
      !pointInsideObstacle(s.x, s.y, geometry, floorWidthM, floorHeightM) &&
      !pointOnWallLine(s.x, s.y, geometry.walls) &&
      isServiceableCeilingApSite(s.x, s.y, geometry, 'ceiling'),
  )

  const sorted = [...aps].sort((a, b) => a.y - b.y || a.x - b.x)
  return sorted.map((ap, i) => {
    const mount = normalizeMountType(ap.mount)
    const slot = validSlots[i % Math.max(1, validSlots.length)] ?? clampToFloorBounds(ap.x, ap.y, geometry)

    if (mount === 'wall' && geometry.walls.length > 0) {
      const wall = geometry.walls[i % geometry.walls.length]
      const snap = wallInwardOffset(wall, floorWidthM, floorHeightM, slot)
      return {
        ...ap,
        x: snap.x,
        y: snap.y,
        mountOrientationDeg: snap.orientDeg,
        mountHeightM: ap.mountHeightM ?? defaultMountHeight('wall'),
      }
    }

    if (mount === 'ceiling' && geometry.rooms.length > 0) {
      const room = geometry.rooms[i % geometry.rooms.length]
      return {
        ...ap,
        x: clamp(room.x + room.w / 2, bounds.xMin, bounds.xMax),
        y: clamp(room.y + room.h / 2, bounds.yMin, bounds.yMax),
        mountHeightM: ap.mountHeightM ?? defaultMountHeight('ceiling'),
      }
    }

    return {
      ...ap,
      x: slot.x,
      y: slot.y,
      mountHeightM: ap.mountHeightM ?? defaultMountHeight(mount),
    }
  })
}

export function geometryModelSummary(geometry: FloorGeometry): string | null {
  const parts: string[] = []
  if (geometry.walls.length > 0) {
    parts.push(`${geometry.walls.length} wall segment${geometry.walls.length !== 1 ? 's' : ''} (height & material)`)
  }
  if (geometry.obstacles.length > 0) {
    parts.push(`${geometry.obstacles.length} obstacle${geometry.obstacles.length !== 1 ? 's' : ''} (3D clearance)`)
  }
  if (geometry.rooms.length > 0) {
    parts.push(`${geometry.rooms.length} room${geometry.rooms.length !== 1 ? 's' : ''}`)
  }
  return parts.length > 0 ? parts.join(', ') : null
}
